<template>
  <a-upload
    name="file"
    :accept="accept"
    :file-list="internalFileList"
    :remove="handleRemove"
    :before-upload="handleBeforeUpload"
    :custom-request="handleCustomUpload"
    :disabled="disabled"
  >
    <a-button :disabled="uploading" class="my-5 upload_new">
      {{ uploadText }}
    </a-button>
  </a-upload>
</template>

<script>
import { uploadFile } from '@/api/saiti'

export default {
  name: 'FileUploadNew',
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: [String, Array],
      default: () => []
    },
    accept: {
      type: String,
      default: '.zip,.rar,.7z'
    },
    maxUplaod: {
      type: Number,
      default: 1
    },
    uploadText: {
      type: String,
      default: '文件上传'
    },
    disabled: Boolean,
    maxSize: {
      type: Number,
      default: 1024 * 1024 * 100 // 默认 100MB
    }
  },
  data() {
    return {
      uploading: false,
      internalFileList: []
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (!newVal) {
          this.internalFileList = []
          return
        }

        if (typeof newVal === 'string') {
          if (newVal === '') {
            this.internalFileList = []
            return
          }
          const arr = newVal.split(',')
          this.internalFileList = arr.filter(url => url.trim()).map((item, index) => {
            const url = item.trim()
            const fileName = url.split('/').pop() || `file-${index}`
            return {
              uid: `legacy-${index}-${Date.now()}`,
              name: fileName,
              status: 'done',
              url: url,
              response: {
                url: url,
                status: 'done'
              }
            }
          })
          return
        }

        // 处理数组类型的value
        this.internalFileList = newVal.map((item, index) => {
          if (typeof item === 'string') {
            return {
              uid: `file-${index}-${Date.now()}`,
              name: item.split('/').pop() || `file-${index}`,
              status: 'done',
              url: item,
              response: {
                url: item,
                status: 'done'
              }
            }
          }
          return {
            uid: item.uid || `file-${index}-${Date.now()}`,
            name: item.name || `file-${index}`,
            status: item.status || 'done',
            url: item.url || item.response?.url,
            response: item.response || { url: item.url, status: 'done' }
          }
        })
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    normFile(e) {
      if (Array.isArray(e)) {
        return e
      }
      return e && e.fileList
    },

    handleRemove(file) {
      const index = this.internalFileList.findIndex(f => f.uid === file.uid)
      if (index !== -1) {
        this.internalFileList.splice(index, 1)
        this.emitChange()
      }
    },

    handleBeforeUpload(file) {
      if (this.internalFileList.length >= this.maxUplaod) {
        this.$message.error('上传文件数量已达到上限')
        return false
      }

      const fileExt = `.${file.name.split('.').pop()}`
      const isValidType = this.accept.split(',').some(ext => ext.trim() === fileExt)
      if (!isValidType) {
        this.$message.error(`文件类型不符合要求，仅支持 ${this.accept} 格式`)
        return false
      }

      const isValidSize = file.size <= this.maxSize
      if (!isValidSize) {
        this.$message.error(`文件大小超过限制，最大支持 ${this.maxSize / 1024 / 1024}MB`)
        return false
      }

      this.internalFileList = [...this.internalFileList, {
        uid: file.uid,
        name: file.name,
        status: 'uploading',
        percent: 0,
        originFileObj: file
      }]

      this.emitChange()
      return true
    },

    async handleCustomUpload({ file, onSuccess, onError, onProgress }) {
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', file)

        // 更新上传进度
        const updateProgress = (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          const index = this.internalFileList.findIndex(f => f.uid === file.uid)
          if (index !== -1) {
            this.internalFileList[index].percent = percent
          }
          onProgress({ percent }, file)
        }

        const { success, result, message } = await uploadFile(formData, {
          onUploadProgress: updateProgress
        })

        this.uploading = false

        if (success) {
          const fileItem = {
            uid: file.uid,
            name: file.name,
            status: 'done',
            url: result,
            response: {
              url: result,
              status: 'done'
            }
          }

          const index = this.internalFileList.findIndex(f => f.uid === file.uid)
          if (index !== -1) {
            this.internalFileList.splice(index, 1, fileItem)
          } else {
            this.internalFileList.push(fileItem)
          }

          onSuccess(fileItem, file)
          this.emitChange()
        } else {
          throw new Error(message || '上传失败')
        }
      } catch (err) {
        this.uploading = false
        const index = this.internalFileList.findIndex(f => f.uid === file.uid)
        if (index !== -1) {
          this.internalFileList.splice(index, 1, {
            uid: file.uid,
            name: file.name,
            status: 'error',
            response: {
              status: 'error',
              message: err.message
            }
          })
          this.emitChange()
        }
        onError(err)
      }
    },

    emitChange() {
      const doneFiles = this.internalFileList
        .filter(file => file.status === 'done')
        .map(file => file.url || file.response?.url)

      // 根据父组件需要的格式发送数据
      const valueToEmit = this.maxUplaod === 1
        ? doneFiles[0] || ''
        : doneFiles.join(',')

      this.$emit('change', valueToEmit)
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep .upload_new {
  position: relative;
}
</style>
